import { useEffect, useMemo, useState } from "react";
import {
  getAdminExEmployeeLeaveBalancesAPI,
  getAdminLeaveBalancesAPI,
  updateAdminLeaveBalanceAPI,
} from "../../../Redux/Apis/leaveApi";
import { CEO_ROLE_ID, CMO_ROLE_ID } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import Loader from "../../atoms/Loader/Loader";
import moment from "moment";

const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatAccrualMonthLabel = (yearMonth) => {
  if (!yearMonth) return "—";
  const [year, month] = String(yearMonth).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return yearMonth;
  return date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const formatDateLabel = (value) => {
  if (!value) return "—";
  const parsed = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY") : String(value);
};

const LeaveBalanceAdminPanel = ({ variant = "active" }) => {
  const isExEmployeeView = variant === "ex";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);
  const [search, setSearch] = useState("");

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = isExEmployeeView
        ? await getAdminExEmployeeLeaveBalancesAPI()
        : await getAdminLeaveBalancesAPI();
      const list = Array.isArray(response?.data) ? response.data : [];
      setRows(
        list
          .filter((item) => {
            const roleId = Number(item?.roleId);
            return roleId !== CMO_ROLE_ID && roleId !== CEO_ROLE_ID;
          })
          .map((item) => ({
            ...item,
            manualUsedLeavesInput: String(toNum(item.manualUsedLeaves)),
            manualUsedMonthInput: item.manualUsedYearMonth || "",
            manualReduceInput: String(
              toNum(item.manualReduceLeaves ?? item.manualBalanceAdjustment)
            ),
            noteInput: item.adjustmentNote || "",
            accrualStartInput: item.accrualStartYearMonth || "",
            accrualStopInput: item.accrualStopYearMonth || "",
          }))
      );
    } catch (error) {
      showErrorToast(
        error?.message
        || (isExEmployeeView
          ? "Failed to fetch ex-employee leave balances."
          : "Failed to fetch employee leave balances.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [variant]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const fullName = `${row.firstname || ""} ${row.lastname || ""}`.trim().toLowerCase();
      return fullName.includes(q) || String(row.email || "").toLowerCase().includes(q);
    });
  }, [rows, search]);

  const updateRowField = (userId, key, value) => {
    setRows((prev) => prev.map((row) => (row.userId === userId ? { ...row, [key]: value } : row)));
  };

  const handleSave = async (row) => {
    try {
      setSavingUserId(row.userId);
      const response = await updateAdminLeaveBalanceAPI({
        userId: row.userId,
        manualUsedLeaves: toNum(row.manualUsedLeavesInput),
        manualUsedYearMonth: row.manualUsedMonthInput || "",
        manualReduce: toNum(row.manualReduceInput),
        accrualStartYearMonth: row.accrualStartInput || "",
        accrualStopYearMonth: row.accrualStopInput || "",
        note: row.noteInput || "",
      });
      const updated = response?.data;
      setRows((prev) =>
        prev.map((it) =>
          it.userId === row.userId
            ? {
              ...it,
              ...updated,
              manualUsedLeavesInput: String(toNum(updated?.manualUsedLeaves)),
              manualUsedMonthInput: updated?.manualUsedYearMonth || "",
              manualReduceInput: String(
                toNum(updated?.manualReduceLeaves ?? updated?.manualBalanceAdjustment)
              ),
              noteInput: updated?.adjustmentNote || "",
              accrualStartInput: updated?.accrualStartYearMonth || "",
              accrualStopInput: updated?.accrualStopYearMonth || "",
            }
            : it
        )
      );
      showSuccessToast("Leave balance updated.");
    } catch (error) {
      showErrorToast(error?.message || "Failed to update leave balance.");
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="leave-balance-admin-panel__loader">
        <Loader />
      </div>
    );
  }

  return (
    <section className="leave-balance-admin-panel">
      <div className="leave-balance-admin-panel__head">
        <div>
          <h3>
            {isExEmployeeView
              ? "Ex-employee Leave Balances (Finance Year)"
              : "Employee Leave Balances (Finance Year)"}
          </h3>
          <p className="leave-balance-admin-panel__hint">
            {isExEmployeeView ? (
              <>
                Deleted employees remain listed here with their leave history and balance for the
                current finance year. Last working day is shown when recorded at deletion.
              </>
            ) : (
              <>
                By default paid leave accrues from April each finance year. Set a start month for new joiners
                (e.g. after probation) and a stop month for notice period (no new accrual after that month).
                During notice, employees can still use remaining paid balance; leave beyond balance is unpaid.
                <strong> Paid used</strong> is leave counted against accrued paid leave (approved, HR attendance, manual).
                The Manual used column is for extra days not captured in leave requests or attendance.
                WFH requests are never counted as leave.
              </>
            )}
          </p>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email"
        />
      </div>
      <div className="leave-balance-admin-panel__table-wrap">
        <table className="leave-balance-admin-panel__table">
          <thead>
            <tr>
              <th>Employee</th>
              {isExEmployeeView ? <th>Last working day</th> : null}
              <th>Accrued</th>
              <th title="Net paid leave used from the bank (system + Manual used − Manual reduce). Accrued − Paid used = Available.">
                Paid used
              </th>
              <th title="Accrued − Paid used">
                Available
              </th>
              <th>Accrual from</th>
              <th>Accrual until</th>
              <th title="HR credit: increases available paid leave">
                Manual reduce
              </th>
              <th title="HR leave for a specific month (does not eat later months' accrual)">
                Manual used
              </th>
              <th title="Month this Manual used applies to (e.g. July 2026)">
                Manual used month
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredRows.length ? filteredRows.map((row) => (
              <tr key={row.userId}>
                <td>
                  <div className="leave-balance-admin-panel__employee">
                    <strong>{`${row.firstname || ""} ${row.lastname || ""}`.trim() || row.email}</strong>
                    <span>{row.email}</span>
                    {isExEmployeeView ? (
                      <span className="leave-balance-admin-panel__ex-badge">Ex-employee</span>
                    ) : null}
                    {(row.usesManualAccrualStart || row.usesManualAccrualStop) && (
                      <span className="leave-balance-admin-panel__accrual-status">
                        {row.usesManualAccrualStart
                          ? `Accruing from ${formatAccrualMonthLabel(row.effectiveAccrualStartYearMonth)}`
                          : "Accruing from April"}
                        {row.isAccrualStopped ? " · Accrual stopped" : ""}
                      </span>
                    )}
                  </div>
                </td>
                {isExEmployeeView ? (
                  <td>{formatDateLabel(row.lastWorkingDate)}</td>
                ) : null}
                <td>{toNum(row.accruedLeaves).toFixed(2)}</td>
                <td>{toNum(row.paidLeaveUsed).toFixed(2)}</td>
                <td>{Math.max(0, toNum(row.availableLeaves)).toFixed(2)}</td>
                <td>
                  <input
                    type="month"
                    value={row.accrualStartInput}
                    onChange={(e) => updateRowField(row.userId, "accrualStartInput", e.target.value)}
                    title="First month paid leave starts accruing (leave empty for April default)"
                  />
                </td>
                <td>
                  <input
                    type="month"
                    value={row.accrualStopInput}
                    onChange={(e) => updateRowField(row.userId, "accrualStopInput", e.target.value)}
                    title="Last month paid leave accrues (e.g. notice period — no new accrual after this month)"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={row.manualReduceInput}
                    onChange={(e) => updateRowField(row.userId, "manualReduceInput", e.target.value)}
                    title="Days to subtract from used leave balance (increases available paid leave)"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={row.manualUsedLeavesInput}
                    onChange={(e) => updateRowField(row.userId, "manualUsedLeavesInput", e.target.value)}
                    title="HR leave days for the Manual used month"
                  />
                </td>
                <td>
                  <input
                    type="month"
                    value={row.manualUsedMonthInput}
                    onChange={(e) => updateRowField(row.userId, "manualUsedMonthInput", e.target.value)}
                    title="Month Manual used applies to (defaults to previous month if empty)"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => handleSave(row)}
                    disabled={savingUserId === row.userId}
                  >
                    {savingUserId === row.userId ? "Saving..." : "Save"}
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={isExEmployeeView ? 11 : 10} className="leave-balance-admin-panel__empty">
                  {isExEmployeeView
                    ? "No deleted employees found."
                    : "No active employees found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default LeaveBalanceAdminPanel;
